// This file contains logic around checking whether or not media file paths actually exist.
// It's wrapped with a custom caching/deduping layer (see `./cache.ts`).
// Very similar to `./directURL.ts`.

import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';

import {
  AvailableFilesQueryQuery,
  AvailableFilesQueryQueryVariables,
} from '../../../../generated/graphql';
import {AVAILABLE_FILES_QUERY} from '../../../../graphql/media';
import {fetchGQL} from '../util';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {BaseFetchParams} from '.';
import {fetchWithCache} from './cache';

type FilePathExistence = boolean | null;

const filePathExistencePromiseByFilePathKey: Map<
  string,
  Promise<FilePathExistence>
> = new Map();

export async function fetchExistingFilePathSet({
  runSignature,
  filePaths,
}: BaseFetchParams): ReturnType<typeof fetchExistingFilePathSetUncached> {
  return fetchWithCache({
    runSignature,
    filePaths,
    pathPromiseByKey: filePathExistencePromiseByFilePathKey,
    fetchNew: fetchExistingFilePathSetUncached,
    getPromiseForPath: getExistencePromiseForPath,
    getReturnValue: makeExistingFilePathSet,
  });
}

async function fetchExistingFilePathSetUncached({
  runSignature,
  filePaths,
}: BaseFetchParams): Promise<Set<string> | null> {
  if (filePaths.length === 0) {
    return new Set();
  }

  const data = await fetchGQL<
    AvailableFilesQueryQueryVariables,
    AvailableFilesQueryQuery
  >({
    query: AVAILABLE_FILES_QUERY,
    variables: {
      ...runSignature,
      filenames: filePaths,
      includeDirectUrl: false,
    },
  });

  const existingFilePaths =
    data?.project?.run?.files?.edges
      ?.map(e => e.node)
      .filter(isNotNullOrUndefined)
      .filter(f => f.sizeBytes > 0)
      .map(f => f.name) ?? [];

  return new Set(existingFilePaths);
}

async function getExistencePromiseForPath(
  existingSetPromise: ReturnType<typeof fetchExistingFilePathSetUncached>,
  filePath: string
): Promise<FilePathExistence> {
  const existingSet = await existingSetPromise;
  if (existingSet == null) {
    return null;
  }
  return existingSet.has(filePath);
}

function makeExistingFilePathSet(
  filePaths: string[],
  filePathExistences: FilePathExistence[]
): Set<string> {
  return new Set(filePaths.filter((filePath, i) => filePathExistences[i]));
}
